import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/apiClient';
import type {
  TemplateListParams,
  TemplateListResponse,
} from '../../lib/types/template.types';

/**
 * Query key factory for templates
 * Ensures consistent cache keys across the app
 */
export const templateKeys = {
  all: ['templates'] as const,
  lists: () => [...templateKeys.all, 'list'] as const,
  list: (params: TemplateListParams) => [...templateKeys.lists(), params] as const,
  details: () => [...templateKeys.all, 'detail'] as const,
  detail: (id: string) => [...templateKeys.details(), id] as const,
  categories: () => [...templateKeys.all, 'categories'] as const,
};

/**
 * Fetch templates with filters, search, pagination
 *
 * @param params - Filter, search, and pagination parameters
 * @returns Query result with templates list
 *
 * @example
 * const { data, isLoading } = useTemplates({ search: 'welcome', is_favorite: true });
 */
export function useTemplates(params: TemplateListParams = {}) {
  return useQuery({
    queryKey: templateKeys.list(params),
    queryFn: async () => {
      // Build query params
      const queryParams: Record<string, string> = {};

      if (params.search) queryParams.search = params.search;
      if (params.category_id) queryParams.category_id = params.category_id;
      if (params.tags && params.tags.length > 0) queryParams.tags = params.tags.join(',');
      if (params.is_favorite !== undefined) queryParams.is_favorite = String(params.is_favorite);
      if (params.limit) queryParams.limit = String(params.limit);
      if (params.offset) queryParams.offset = String(params.offset);
      if (params.sort_by) queryParams.sort_by = params.sort_by;
      if (params.sort_order) queryParams.sort_order = params.sort_order;

      const { data } = await api.get<TemplateListResponse>('/api/templates', { params: queryParams });
      return data;
    },
    staleTime: 1000 * 60, // 1 minute - templates don't change often
  });
}
